/*
 * <BeScrollBar> -- React wrapper for the .be-scrollbar CSS component: the
 * BeOS R5 scrollbar, recreated in the DOM (the R5 look is server-side; see
 * components/scrollbar.css and rdef_src/app_server_r5/reconstructed/
 * TScrollBar.cpp for provenance).
 *
 * Controlled: pass value/onChange. The thumb is proportional when
 * `proportion` is set (fraction of the range visible, like
 * BScrollBar::SetProportion), fixed-length otherwise, and never shorter than
 * the R5 scroll_knob_size (15px). Arrows step smallStep (held: repeats),
 * track clicks page bigStep, the thumb drags. An empty range (max <= min)
 * renders the disabled flat track, exactly like the server.
 *
 *   <BeScrollBar orientation="horizontal" value={x} max={400} proportion={0.3} onChange={setX} />
 */

import {
	type CSSProperties,
	type HTMLAttributes,
	type KeyboardEvent,
	type PointerEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { type BeCommon, beComponent } from "./create.tsx";

export type BeScrollBarOrientation = "horizontal" | "vertical";

/** The thumb's grip (scroll_bar_info.knob). */
export type BeScrollKnob = "nubs" | "none";

/* Not BeProps<"div">: `onChange` is a numeric callback, not a DOM event, so the
 * DOM attributes come in with that one omitted. */
export type BeScrollBarProps = BeCommon &
	Omit<HTMLAttributes<HTMLDivElement>, "className" | "style" | "onChange"> & {
		orientation?: BeScrollBarOrientation;
		value?: number;
		min?: number;
		max?: number;
		/** Visible fraction of the range (0..1); omit for a fixed-length thumb. */
		proportion?: number;
		/** Arrow-button step (BScrollBar small step). */
		smallStep?: number;
		/** Track-click step (BScrollBar big step). */
		bigStep?: number;
		/** Grip style, the scroll_knob preference. */
		knob?: BeScrollKnob;
		disabled?: boolean;
		/** Dim the grip highlight (unfocused window); inherited from .be-window.is-inactive too. */
		inactive?: boolean;
		onChange?: (value: number) => void;
		/** Length along the scroll axis (defaults to the CSS 160px). */
		length?: number | string;
		/** Accessible label for the bar and its arrows. */
		label?: string;
		/** id of the element this bar scrolls (aria-controls); <BeScrollView> wires it. */
		controls?: string;
	};

const REPEAT_DELAY_MS = 250;
const REPEAT_INTERVAL_MS = 50;

export const BeScrollBar = beComponent<BeScrollBarProps, HTMLDivElement>("BeScrollBar", (props, be) => {
	const {
		orientation = "vertical",
		value = 0,
		min = 0,
		max = 100,
		proportion,
		smallStep = 1,
		bigStep = 10,
		knob = "nubs",
		disabled,
		inactive,
		onChange,
		length,
		label,
		controls,
		// consumed by be() off `props`; named here only to keep them out of the DOM
		scale: _scale,
		className: _className,
		style: _style,
		...rest
	} = props;

	const horizontal = orientation === "horizontal";
	const empty = max <= min;
	const isDisabled = disabled || empty;

	const trackRef = useRef<HTMLDivElement>(null);
	const [trackLen, setTrackLen] = useState(0);

	// Track length in CSS px (ResizeObserver keeps it fresh through flex
	// re-layout); the thumb minimum and the is-wide threshold derive from the
	// bar's cross thickness (15 BeOS-px), so they follow --be-scale for free.
	const [thickness, setThickness] = useState(15);
	useLayoutEffect(() => {
		const el = trackRef.current;
		if (!el) return;
		const measure = () => {
			const r = el.getBoundingClientRect();
			setTrackLen(horizontal ? r.width : r.height);
			const bar = el.parentElement?.getBoundingClientRect();
			if (bar) setThickness(horizontal ? bar.height : bar.width);
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [horizontal]);

	const bePx = thickness / 15;
	const thumbMin = 15 * bePx;
	const range = Math.max(1, max - min);
	const frac = proportion !== undefined && proportion > 0 ? Math.min(1, proportion) : bigStep / (range + bigStep);
	const thumbLen = Math.min(trackLen, Math.max(thumbMin, frac * trackLen));
	const free = Math.max(0, trackLen - thumbLen);
	const clamp = useCallback((v: number) => Math.max(min, Math.min(max, v)), [min, max]);
	const pos = empty ? 0 : ((clamp(value) - min) / range) * free;

	// arrow autorepeat
	const repeat = useRef<{ timer: ReturnType<typeof setTimeout> | null }>({ timer: null });
	const stopRepeat = useCallback(() => {
		if (repeat.current.timer !== null) clearTimeout(repeat.current.timer);
		repeat.current.timer = null;
	}, []);
	useEffect(() => stopRepeat, [stopRepeat]);

	const valueRef = useRef(value);
	valueRef.current = value;

	const startStep = (dir: -1 | 1) => (e: PointerEvent<HTMLButtonElement>) => {
		if (isDisabled || !onChange) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const fire = () => onChange(clamp(valueRef.current + dir * smallStep));
		fire();
		const tick = (delay: number) => {
			repeat.current.timer = setTimeout(() => {
				fire();
				tick(REPEAT_INTERVAL_MS);
			}, delay);
		};
		tick(REPEAT_DELAY_MS);
	};

	const onTrackDown = (e: PointerEvent<HTMLDivElement>) => {
		if (isDisabled || !onChange || e.target !== e.currentTarget) return;
		const r = e.currentTarget.getBoundingClientRect();
		const p = horizontal ? e.clientX - r.left : e.clientY - r.top;
		onChange(clamp(value + (p < pos ? -bigStep : bigStep)));
	};

	const onThumbDown = (e: PointerEvent<HTMLDivElement>) => {
		if (isDisabled || !onChange || free <= 0) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const start = horizontal ? e.clientX : e.clientY;
		const startValue = clamp(value);
		const onMove = (ev: globalThis.PointerEvent) => {
			const d = (horizontal ? ev.clientX : ev.clientY) - start;
			onChange(clamp(startValue + (d * range) / free));
		};
		const onUp = () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (isDisabled || !onChange) return;
		const lo = horizontal ? "ArrowLeft" : "ArrowUp";
		const hi = horizontal ? "ArrowRight" : "ArrowDown";
		let next: number | null = null;
		if (e.key === lo) next = value - smallStep;
		else if (e.key === hi) next = value + smallStep;
		else if (e.key === "PageUp") next = value - bigStep;
		else if (e.key === "PageDown") next = value + bigStep;
		else if (e.key === "Home") next = min;
		else if (e.key === "End") next = max;
		if (next === null) return;
		e.preventDefault();
		onChange(clamp(next));
	};

	const root = be(
		"be-scrollbar",
		horizontal ? "be-scrollbar--horizontal" : "be-scrollbar--vertical",
		knob === "none" && "be-scrollbar--knob-none",
		isDisabled && "is-disabled",
		inactive && "is-inactive",
	);

	const rootStyle: CSSProperties = { ...root.style };
	if (length !== undefined) {
		if (horizontal) rootStyle.width = length;
		else rootStyle.height = length;
	}

	const thumbStyle: CSSProperties = {
		"--be-sb-thumb-pos": `${pos}px`,
		"--be-sb-thumb-len": `${thumbLen}px`,
	};

	const decLabel = horizontal ? "Scroll left" : "Scroll up";
	const incLabel = horizontal ? "Scroll right" : "Scroll down";
	const arrow = (dir: -1 | 1, key: string) => (
		<button
			key={key}
			type="button"
			tabIndex={-1}
			className={`be-scrollbar__arrow be-scrollbar__arrow--${dir === -1 ? "dec" : "inc"}`}
			aria-label={dir === -1 ? decLabel : incLabel}
			disabled={isDisabled}
			onPointerDown={startStep(dir)}
			onPointerUp={stopRepeat}
			onPointerCancel={stopRepeat}
		/>
	);

	return (
		<div
			{...rest}
			{...root}
			style={rootStyle}
			role="scrollbar"
			tabIndex={isDisabled ? undefined : 0}
			aria-label={label}
			aria-controls={controls ?? ""}
			aria-orientation={orientation}
			aria-valuemin={min}
			aria-valuemax={max}
			aria-valuenow={clamp(value)}
			aria-disabled={isDisabled || undefined}
			onKeyDown={onKeyDown}
		>
			{arrow(-1, "dec")}
			<div className="be-scrollbar__track" ref={trackRef} onPointerDown={onTrackDown}>
				{!empty && (
					<div className="be-scrollbar__thumb" style={thumbStyle} onPointerDown={onThumbDown}>
						{knob !== "none" && thumbLen > 12 * bePx && (
							<div className="be-scrollbar__grip">
								{/* draw_thumb: 1 nub past 12px, 3 past 25px */}
								{(thumbLen > 25 * bePx ? [0, 1, 2] : [0]).map((i) => (
									<i key={i} className="be-scrollbar__nub" />
								))}
							</div>
						)}
					</div>
				)}
			</div>
			{arrow(1, "inc")}
		</div>
	);
});
